import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, cents, errMsg } from "../lib/api";
import { useCart } from "../context/CartContext";
import usePageMeta from "../hooks/usePageMeta";
import { track } from "../lib/analytics";
import { MAX_BOTTLES, priceFor, stackLabel, functionalBreakdown } from "../lib/pricing";

function BulkInquiry() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", fulfillment: "pickup", notes: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bulk-inquiries", form);
      track("request_bulk", {});
      setSent(true);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section id="bulk" className="blush-panel border border-[#EBD9D0] p-5 sm:p-8 mt-14" data-testid="bulk-inquiry-section">
      <p className="eyebrow mb-2">Events &amp; teams</p>
      <h2 className="text-2xl sm:text-3xl mb-3">Bulk Orders — 36 Bottles for $360</h2>
      <p className="text-sm text-[#171715]/75 max-w-2xl leading-relaxed mb-1">Planning for a team, studio or event? Bulk orders of 36 bottles are $360 (reference pricing). They need <strong>at least 48 hours' notice</strong> and are confirmed only once pickup or delivery availability is scheduled — so we take them as requests rather than instant checkout. Nothing is charged online.</p>
      <p className="text-xs text-[#171715]/55 mb-5">Standard online orders remain up to {MAX_BOTTLES} bottles with at least 24 hours' notice.</p>
      {sent ? (
        <p data-testid="bulk-inquiry-success" className="card-paper p-4 text-sm" role="status">Request received — we'll confirm availability and scheduling with you directly. Thank you!</p>
      ) : (
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3 max-w-2xl">
          <input data-testid="bulk-name-input" className="input-line" placeholder="Your name" value={form.name} onChange={set("name")} aria-label="Your name" />
          <input data-testid="bulk-email-input" className="input-line" type="email" required placeholder="you@example.com" value={form.email} onChange={set("email")} aria-label="Email address" />
          <input data-testid="bulk-phone-input" className="input-line" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} aria-label="Phone number (optional)" />
          <input data-testid="bulk-date-input" className="input-line" type="date" value={form.preferred_date} onChange={set("preferred_date")} aria-label="Preferred date" />
          <select data-testid="bulk-fulfillment-select" className="input-line" value={form.fulfillment} onChange={set("fulfillment")} aria-label="Pickup or delivery">
            <option value="pickup">Pickup (Dallas)</option>
            <option value="delivery">DFW delivery (by arrangement)</option>
          </select>
          <textarea data-testid="bulk-notes-input" className="input-line sm:col-span-2" rows="3" placeholder="Flavors, event details, anything we should know" value={form.notes} onChange={set("notes")} aria-label="Notes" />
          <button data-testid="bulk-submit-button" className="btn-ink sm:col-span-2" disabled={busy}>{busy ? "Sending…" : "Request bulk order"}</button>
        </form>
      )}
    </section>
  );
}

export default function BuildStack() {
  usePageMeta("Mix & Match Juice Packs — Build Your Stack | Tiff Wellness Co.", "Mix and match any number of juices up to 24. Functional 3-pack ($28) and 6-pack ($54) savings apply automatically. Choose Dallas pickup at checkout.");
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState({});
  const { replace, setDrawerOpen } = useCart();
  const navigate = useNavigate();
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  const total = useMemo(() => Object.values(sel).reduce((a, b) => a + b, 0), [sel]);
  const nf = useMemo(() => products.length ? Object.entries(sel).reduce((a, [s, q]) => a + ((products.find((p) => p.slug === s)?.category === "traditional") ? 0 : q), 0) : 0, [sel, products]);
  const nt = total - nf;
  const price = priceFor(nf, nt);

  const addSel = (slug) => {
    if (total >= MAX_BOTTLES) { toast.info(`Maximum ${MAX_BOTTLES} bottles per order`); return; }
    if (total === 0) track("bundle_started", { bundle_key: "custom" });
    setSel((p) => ({ ...p, [slug]: (p[slug] || 0) + 1 }));
  };
  const decSel = (slug) => setSel((p) => {
    const n = { ...p }; if (!n[slug]) return p; n[slug] -= 1; if (n[slug] <= 0) delete n[slug]; return n;
  });

  const continueOneTime = () => {
    replace(sel); setDrawerOpen(false); navigate("/cart");
  };
  const continueRecurring = () => {
    localStorage.setItem("tw_sub_prefill", JSON.stringify({ bottles: total, sel }));
    navigate("/subscriptions");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <p className="eyebrow mb-2">Bundle builder</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-3">Build Your Juice Stack</h1>
      <p className="text-sm text-[#171715]/70 max-w-2xl mb-8">Add as many bottles as you like (up to {MAX_BOTTLES}) — repeats welcome. Functional juices ($12) earn 3-pack and 6-pack savings automatically; Renew+ (traditional, $10) is priced per bottle while traditional pack pricing is pending confirmation.</p>

      <div className="card-paper p-5 mb-8 max-w-3xl" data-testid="build-your-own-info">
        <p className="eyebrow mb-2">Build-your-own at home</p>
        <h2 className="text-lg font-medium mb-2">Traditional juice + your own add-ins</h2>
        <p className="text-sm text-[#171715]/75 leading-relaxed">Prefer to control your own scoop? Pair a traditional juice like Renew+ with separately available 1st Phorm Clear Protein or Creatine and add it at home. Supplement pricing and package details are pending — this is informational only and nothing is added to your cart. <Link to="/contact" className="underline" data-testid="supplement-info-link">Request supplement info</Link>.</p>
        <p className="text-xs text-[#171715]/55 mt-2">Ready-to-drink functional blends (Hydrate+, Citrus Drive, Flow+, Tropical Recovery, Electric Sun) already include their listed add-in — these are different from add-at-home supplements.</p>
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10">
        <div>
          <h2 className="text-lg font-medium mb-3">Pick Your Juices</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {products.map((p) => (
              <div key={p.slug} data-testid={`bundle-product-${p.slug}`} className="card-paper p-3 flex items-center gap-2 min-w-0 overflow-hidden">
                <img src={p.image} alt="" width="30" height="100" className="h-14 w-auto object-contain shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{p.name}</p>
                  <p className="text-xs text-[#171715]/60 truncate capitalize">{p.ingredients.join(", ")}</p>
                  {p.functional_addition && <p className="text-[10px] font-mono uppercase text-[#A35847] truncate">+ {p.functional_addition}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {sel[p.slug] > 0 && <button data-testid={`bundle-remove-juice-${p.slug}`} aria-label={`Remove ${p.name}`} className="min-h-[44px] min-w-[44px] border border-[#EBD9D0]" onClick={() => decSel(p.slug)}>−</button>}
                  {sel[p.slug] > 0 && <span className="font-mono text-sm w-5 text-center">{sel[p.slug]}</span>}
                  <button data-testid={`bundle-add-juice-${p.slug}`} aria-label={`Add ${p.name}`} disabled={!p.available || total >= MAX_BOTTLES}
                    className="min-h-[44px] min-w-[44px] bg-[#171715] text-[#FBF6F1] disabled:opacity-30" onClick={() => addSel(p.slug)}>+</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <aside className="lg:sticky lg:top-24 h-fit card-paper p-5">
          <h2 className="text-lg font-medium mb-3">Review Your Order</h2>
          <p data-testid="bundle-bottle-count" className="text-sm mb-2">{total === 0 ? "No bottles yet — add your first juice" : `${total} bottle${total > 1 ? "s" : ""} selected`}</p>
          <ul className="text-sm text-[#171715]/80 space-y-1 mb-3">
            {Object.entries(sel).map(([slug, q]) => <li key={slug} className="flex justify-between"><span>{products.find((p) => p.slug === slug)?.name || slug.replace(/-/g, " ")}</span><span className="font-mono">×{q}</span></li>)}
          </ul>
          {total > 0 && (
            <>
              <p className="flex justify-between font-semibold border-t border-[#EBD9D0] pt-3 text-sm"><span>{stackLabel(total)}</span><span data-testid="bundle-running-total" className="font-mono">{cents(price)}</span></p>
              <p className="text-xs text-[#171715]/55 mt-1">
                {nf > 0 && `Functional (${nf}): ${functionalBreakdown(nf)} pricing applied automatically. `}
                {nt > 0 && `Traditional (${nt}): $10 each — pack pricing pending confirmation.`}
              </p>
            </>
          )}
          <p className="text-xs text-[#171715]/55 mt-2">One-Time Purchase and Recurring Pickup are separate, explicit choices.</p>
          <button data-testid="bundle-complete-add-cart-button" className="btn-ink w-full mt-4" disabled={total === 0} onClick={continueOneTime}>One-time purchase →</button>
          <button data-testid="bundle-make-recurring-button" className="btn-ghost w-full mt-2" disabled={total === 0} onClick={continueRecurring}>Make it recurring →</button>
        </aside>
      </div>
      <BulkInquiry />
    </div>
  );
}
